import { LoadingSpinner } from '../shared/components';

// ── Vacancies — a week of open nights per property (UI/UX R16 #5) ───────────
// Rows = active properties (most-vacant first), columns = the 7 nights of the
// picked week. vacant = sellable & empty (the problem to fix — highlighted),
// booked = quiet gray, blocked = striped (owner hold — not sellable).

const esc = (v: any) => String(v == null ? '' : v).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

export class VacanciesPage {
  private container: HTMLElement;
  private data: any = null;
  private start = '';        // '' = server default (this Monday)
  private hideFull = false;  // hide properties with 0 vacant nights
  private loading = false;   // in-flight guard — week nav ignores clicks while fetching

  constructor(container: HTMLElement) { this.container = container; }

  async render(): Promise<void> {
    this.container.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`;
    await this.load();
    this.draw();
  }

  private async load(force = false): Promise<void> {
    const qs = [
      this.start ? `start=${encodeURIComponent(this.start)}` : '',
      force ? 'force=1' : '',
    ].filter(Boolean).join('&');
    try { this.data = await fetch(`/api/vacancies/week${qs ? `?${qs}` : ''}`).then(r => r.ok ? r.json() : null); }
    catch { this.data = null; }
  }

  private shiftWeek(deltaDays: number): string {
    // this.start is the source of truth once the user has navigated —
    // this.data.start lags a fetch behind and would swallow queued clicks.
    const base = this.start || this.data?.start || new Date().toISOString().slice(0, 10);
    const d = new Date(base + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + deltaDays);
    return d.toISOString().slice(0, 10);
  }

  private async goto(start: string, force = false): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    this.start = start;
    this.draw();                       // shows the "pulling live calendars" note
    try { await this.load(force); } finally { this.loading = false; }
    this.draw();
  }

  private draw(): void {
    const d = this.data;
    if (this.loading && !d) {
      this.container.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}<div style="margin-top:10px; font-size:12.5px; color:var(--text-muted);">Pulling live calendars from Hostaway…</div></div>`;
      return;
    }
    if (!d) {
      this.container.innerHTML = `<div class="page-content"><div class="empty-state" style="padding:40px;">Vacancy data unavailable — GET /api/vacancies/week failed.</div></div>`;
      return;
    }
    const asOf = d.as_of ? new Date(d.as_of) : null;
    const asOfStr = asOf && !isNaN(asOf.getTime())
      ? asOf.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : '';
    let rows: any[] = d.rows || [];
    if (this.hideFull) rows = rows.filter((r: any) => r.vacant_nights > 0 || (r.past_open || 0) > 0);
    const dayLabel = (iso: string) => {
      const dt = new Date(iso + 'T00:00:00Z');
      return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dt.getUTCDay()]}<br><span style="font-weight:400; color:var(--text-faint);">${iso.slice(5)}</span>`;
    };
    const cellHtml = (c: any) => {
      const past = c.date < d.today;
      if (c.state === 'vacant') return `<td class="vc-cell vacant ${past ? 'past' : ''}" title="${esc(c.date)} — open night">·</td>`;
      if (c.state === 'booked') return `<td class="vc-cell booked" title="${esc(c.date)} — booked">✓</td>`;
      if (c.state === 'blocked') return `<td class="vc-cell blocked" title="${esc(c.date)} — blocked (not sellable)">▨</td>`;
      return `<td class="vc-cell unknown" title="${esc(c.date)} — no calendar sync">?</td>`;
    };

    this.container.innerHTML = `
      <div class="page-content">
        <style>
          .vc-tbl { width:100%; border-collapse:collapse; font-size:12.5px; background:var(--card); border:1px solid var(--border); border-radius:var(--radius); overflow:hidden; }
          .vc-tbl th { padding:8px 10px; font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); border-bottom:1px solid var(--border); text-align:center; }
          .vc-tbl th:first-child, .vc-tbl td:first-child { text-align:left; position:sticky; left:0; background:var(--card); max-width:220px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
          .vc-tbl td { padding:6px 10px; border-bottom:1px solid var(--border-soft, #f1f5f9); }
          .vc-cell { text-align:center; font-weight:700; width:11%; }
          .vc-cell.vacant { background:#fef3c7; color:#b45309; }
          .vc-cell.vacant.past { background:#f8fafc; color:#cbd5e1; }
          .vc-cell.booked { background:#f8fafc; color:#94a3b8; font-weight:400; }
          .vc-cell.blocked { background:repeating-linear-gradient(45deg, #f1f5f9, #f1f5f9 4px, #e2e8f0 4px, #e2e8f0 8px); color:#64748b; }
          .vc-cell.unknown { color:var(--text-faint); }
          .vc-count { font-weight:700; text-align:center; }
          .vc-key { display:flex; gap:14px; align-items:center; flex-wrap:wrap; margin-bottom:12px; font-size:11.5px; color:var(--text-muted); }
          .vc-key-t { font-weight:700; text-transform:uppercase; letter-spacing:.05em; font-size:10px; }
          .vc-key-item { display:flex; gap:5px; align-items:center; }
          .vc-swatch { display:inline-flex; align-items:center; justify-content:center; width:20px; height:16px; border-radius:4px; border:1px solid var(--border); font-weight:700; font-size:11px; }
        </style>
        <div class="dash-head">
          <h1>Vacancies · week of ${esc(d.start)}</h1>
          <p>${d.totals.vacant_nights} still-bookable open night${d.totals.vacant_nights === 1 ? '' : 's'} across ${d.totals.properties} active properties · ${d.totals.fully_vacant} propert${d.totals.fully_vacant === 1 ? 'y is' : 'ies are'} fully open for the rest of this week.</p>
        </div>
        <div style="display:flex; gap:8px; align-items:center; margin-bottom:8px; flex-wrap:wrap;">
          <button class="btn btn-secondary btn-sm" id="vc-prev" ${this.loading ? 'disabled' : ''}>‹ Prev week</button>
          <button class="btn btn-secondary btn-sm" id="vc-this" ${this.loading ? 'disabled' : ''}>This week</button>
          <button class="btn btn-secondary btn-sm" id="vc-next" ${this.loading ? 'disabled' : ''}>Next week ›</button>
          <span style="font-size:11.5px; color:var(--text-muted);">
            ${this.loading ? 'Pulling live calendars…' : `Live from Hostaway${asOfStr ? ` · updated ${esc(asOfStr)}` : ''}${d.stale_count ? ` · <span style="color:#b45309;">${d.stale_count} propert${d.stale_count === 1 ? 'y' : 'ies'} from last sync (live fetch failed)</span>` : ''}`}
            ${this.loading ? '' : `<a href="#" id="vc-refresh" style="margin-left:4px; color:var(--accent);">refresh</a>`}
          </span>
          <label style="margin-left:auto; font-size:12px; color:var(--text-muted); display:flex; gap:6px; align-items:center; cursor:pointer;">
            <input type="checkbox" id="vc-hidefull" ${this.hideFull ? 'checked' : ''}> hide fully-booked properties
          </label>
        </div>
        <div class="vc-key">
          <span class="vc-key-t">Key:</span>
          <span class="vc-key-item"><span class="vc-swatch" style="background:#fef3c7; color:#b45309;">·</span> open night — bookable</span>
          <span class="vc-key-item"><span class="vc-swatch" style="background:#f8fafc; color:#cbd5e1;">·</span> was open, date passed</span>
          <span class="vc-key-item"><span class="vc-swatch" style="background:#f8fafc; color:#94a3b8; font-weight:400;">✓</span> booked</span>
          <span class="vc-key-item"><span class="vc-swatch" style="background:repeating-linear-gradient(45deg, #f1f5f9, #f1f5f9 4px, #e2e8f0 4px, #e2e8f0 8px); color:#64748b;">▨</span> blocked (owner hold / maintenance)</span>
          <span class="vc-key-item"><span class="vc-swatch" style="color:var(--text-faint);">?</span> no calendar data</span>
        </div>
        <div class="u-table-wrap">
          <table class="vc-tbl">
            <thead><tr>
              <th>Property</th>
              ${d.days.map((day: string) => `<th>${dayLabel(day)}${day === d.today ? '<br><span style="color:#1d4ed8;">today</span>' : ''}</th>`).join('')}
              <th>Open</th>
            </tr></thead>
            <tbody>
              ${rows.map((r: any) => `
                <tr>
                  <td title="${esc(r.name)}${r.stale ? ' — live fetch failed; showing last sync' : ''}">${esc(r.name)}${r.stale ? ' <span style="color:#b45309;" title="live fetch failed; showing last sync">⚠</span>' : ''}</td>
                  ${r.cells.map(cellHtml).join('')}
                  <td class="vc-count" title="Open nights from today onward${r.past_open ? ` (+${r.past_open} already passed, not counted)` : ''}" style="color:${r.vacant_nights >= 5 ? '#dc2626' : r.vacant_nights > 0 ? '#b45309' : '#16a34a'};">${r.vacant_nights}${r.past_open ? `<span style="font-weight:400; color:var(--text-faint); font-size:10.5px;"> +${r.past_open} past</span>` : ''}</td>
                </tr>`).join('')}
              <tr style="font-weight:700; background:var(--bg-subtle);">
                <td>Open properties per night</td>
                ${d.totals.per_day.map((n: number, i: number) => {
                  const past = d.days[i] < d.today;
                  return `<td class="vc-cell" style="background:none; color:${past ? 'var(--text-faint)' : n > 0 ? '#b45309' : '#16a34a'};" title="${past ? 'already passed' : ''}">${n}</td>`;
                }).join('')}
                <td class="vc-count">${d.totals.vacant_nights}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>`;

    this.container.querySelector('#vc-prev')?.addEventListener('click', () => void this.goto(this.shiftWeek(-7)));
    this.container.querySelector('#vc-next')?.addEventListener('click', () => void this.goto(this.shiftWeek(7)));
    this.container.querySelector('#vc-this')?.addEventListener('click', () => void this.goto(''));
    this.container.querySelector('#vc-refresh')?.addEventListener('click', (e) => { e.preventDefault(); void this.goto(this.start, true); });
    this.container.querySelector('#vc-hidefull')?.addEventListener('change', (e) => { this.hideFull = (e.target as HTMLInputElement).checked; this.draw(); });
  }
}
